/**
 * Recursively trim leading/trailing whitespace from string values in plain objects and arrays.
 * Other values (including class instances, Date, Map, etc.) are returned unchanged.
 */
export function trimPayload(data) {
  if (typeof data === "string") {
    return data.trim();
  }
  if (Array.isArray(data)) {
    return data.map(trimPayload);
  }
  if (isPlainObject(data)) {
    const out = {};
    for (const key of Object.keys(data)) {
      out[key] = trimPayload(data[key]);
    }
    return out;
  }
  return data;
}

function isPlainObject(value) {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

const FETCH_PATCH_KEY = "__impactPayloadTrimFetchPatched";

let trimmingInitialized = false;

function trimJsonStringIfParseable(str) {
  if (typeof str !== "string") return str;
  try {
    const parsed = JSON.parse(str);
    return JSON.stringify(trimPayload(parsed));
  } catch {
    return str;
  }
}

/** Bodies we never treat as JSON text (sync or async). */
function isNonJsonFetchBody(body) {
  if (body == null) return true;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  if (typeof Blob !== "undefined" && body instanceof Blob) return true;
  if (typeof File !== "undefined" && body instanceof File) return true;
  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) return true;
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(body)) return true;
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) return true;
  return false;
}

const METHODS_WITH_BODY = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function requestInitFromRequest(source, overrides) {
  const init = {
    method: source.method,
    headers: source.headers,
    mode: source.mode,
    credentials: source.credentials,
    cache: source.cache,
    redirect: source.redirect,
    referrer: source.referrer,
    referrerPolicy: source.referrerPolicy,
    integrity: source.integrity,
    keepalive: source.keepalive,
    signal: source.signal,
  };
  if (source.duplex != null) {
    init.duplex = source.duplex;
  }
  return { ...init, ...overrides };
}

function contentTypeLooksJson(headers) {
  const ct = headers && typeof headers.get === "function" ? headers.get("content-type") : "";
  if (!ct) return false;
  const lower = ct.toLowerCase();
  return lower.includes("application/json") || lower.includes("+json");
}

/**
 * Sync trim when body is a string or plain object in init (url + init form).
 */
function tryTrimUrlAndInit(input, init) {
  if (input instanceof Request) return null;
  const method = (((init && init.method) || "GET") + "").toUpperCase();
  if (!METHODS_WITH_BODY.has(method)) return null;
  const body = init && init.body;
  if (body == null || isNonJsonFetchBody(body)) return null;
  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream) return null;

  if (typeof body === "string") {
    const next = trimJsonStringIfParseable(body);
    if (next === body) return null;
    return { input, init: { ...init, body: next } };
  }
  if (isPlainObject(body)) {
    const next = JSON.stringify(trimPayload(body));
    return { input, init: { ...init, body: next } };
  }
  return null;
}

/**
 * When input is a Request, merge with optional init and trim JSON body (sync if body is string on init override only).
 */
function tryTrimRequestInput(input, init) {
  if (!(input instanceof Request)) return null;
  if (init === undefined) return null;

  const merged = new Request(input, init);
  const method = merged.method.toUpperCase();
  if (!METHODS_WITH_BODY.has(method)) return null;

  const body = init.body;
  if (body === undefined) {
    return null;
  }
  if (body == null || isNonJsonFetchBody(body)) return null;
  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream) return null;

  if (typeof body === "string") {
    const next = trimJsonStringIfParseable(body);
    if (next === body) return null;
    const nextReq = new Request(
      merged.url,
      requestInitFromRequest(merged, { body: next })
    );
    return { input: nextReq, init: undefined };
  }
  if (isPlainObject(body)) {
    const next = JSON.stringify(trimPayload(body));
    const nextReq = new Request(
      merged.url,
      requestInitFromRequest(merged, { body: next })
    );
    return { input: nextReq, init: undefined };
  }
  return null;
}

/**
 * Async: read merged Request body as text when it looks like JSON; otherwise leave original fetch args.
 */
function tryTrimFetchAsync(input, init, nativeFetch) {
  let merged;
  try {
    merged = new Request(input, init);
  } catch {
    return null;
  }

  const method = merged.method.toUpperCase();
  if (!METHODS_WITH_BODY.has(method)) return null;

  if (merged.body == null) return null;
  if (isNonJsonFetchBody(merged.body)) return null;

  const headers = merged.headers;
  if (!contentTypeLooksJson(headers)) return null;

  const clone = merged.clone();

  return clone.text().then((text) => {
    const next = trimJsonStringIfParseable(text);
    if (next === text) {
      return nativeFetch(input, init);
    }
    const nextReq = new Request(
      merged.url,
      requestInitFromRequest(merged, { body: next })
    );
    return nativeFetch(nextReq);
  });
}

function installFetchPayloadTrimming() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  if (window.fetch[FETCH_PATCH_KEY]) return;

  const nativeFetch = window.fetch.bind(window);
  Object.defineProperty(window.fetch, FETCH_PATCH_KEY, {
    value: true,
    configurable: true,
    enumerable: false,
    writable: false,
  });

  window.fetch = function patchedFetch(input, init) {
    const fromUrl = tryTrimUrlAndInit(input, init);
    if (fromUrl) {
      return nativeFetch(fromUrl.input, fromUrl.init);
    }

    const fromReq = tryTrimRequestInput(input, init);
    if (fromReq) {
      return nativeFetch(fromReq.input, fromReq.init);
    }

    // Sync paths above already handle the common case: fetch(url, { body: JSON.stringify(...) }).
    // The async path (cloning Request, reading body as text) added significant latency to every
    // request and is removed. Fall through to native fetch for all remaining cases.
    return nativeFetch(input, init);
  };
}

/**
 * Installs global request payload trimming for fetch.
 * Safe to call multiple times (idempotent).
 */
export function initializePayloadTrimming() {
  if (trimmingInitialized) return;
  trimmingInitialized = true;

  installFetchPayloadTrimming();
}
